/**
 * Component for the search page, which can be accessed at /search.
 */
import "../../stylesheets/shared-package/shared.less";
import "../../stylesheets/search-package/search.less";
import "../../stylesheets/typeahead-package/typeahead.less";

import {t} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";
import classnames from "classnames";
import {compose} from "mu-lambda";
import * as React from "react";

import Banner from "@khanacademy/wonder-blocks-banner";
import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {spacing} from "@khanacademy/wonder-blocks-tokens";
import {Title} from "@khanacademy/wonder-blocks-typography";
import {usePublishAnalyticsEvent} from "@khan/analytics-learning-time";
import {RequestInfo} from "@khan/shared-core";
import {a11yStyles, mediaQueries} from "@khan/shared-styles";

import PageRoot from "#app-shell-package/page-root.tsx";
import withApollo, {
    type WithApolloProps,
    type WithoutApollo,
} from "#data-access/gql/with-apollo.tsx";
import withLocation, {
    type WithLocationProps,
    type WithoutLocation,
} from "#data-access/with-location.tsx";
import ComputerProgramBrowseButton from "#search-package/indexed-search/computer-programs-browse-button.tsx";
import SearchBoxOnly from "#typeahead-package/search-box-only.tsx";
import {initSuggestions} from "#typeahead-package/suggestion-source.ts";

import SearchEngines from "./cse-search-engines.ts";
import GoogleCustomSearchResults from "./google-custom-search-results.tsx";
import IndexedSearch from "./indexed-search.tsx";
import {IsIndexedSearchEnabledForLocale} from "./indexed-search/is-indexed-search-enabled-for-locale.ts";
import TryThisButton from "./indexed-search/try-this-button.tsx";
import {withStrings} from "./locales/search-page/load-strings.tsx";

type Props = WithLocationProps & WithApolloProps;

const SearchPage = (props: Props): React.ReactNode => {
    const [state, setState] = React.useState({
        acceptedSpellingCorrection: "",
        indexedSearchEnabled: IsIndexedSearchEnabledForLocale(
            RequestInfo.kaLocale,
        ),
        isBannerDismissed: false,
    });
    const publishAnalyticsEvent = usePublishAnalyticsEvent();

    const searchBoxElement = React.useRef<SearchBoxOnly | null>(null);

    React.useEffect(() => {
        initSuggestions(props.gqlClient);
    }, [props.gqlClient]);

    const onSearchBoxComponentReady = () => {
        if (searchBoxElement.current && state.acceptedSpellingCorrection) {
            searchBoxElement.current.updateQueryWithAcceptedSpelling(
                state.acceptedSpellingCorrection,
            );
        }
    };

    const handleAcceptSpellingCorrection = (
        acceptedSpellingCorrection: string,
    ) => {
        if (searchBoxElement.current) {
            searchBoxElement.current.updateQueryWithAcceptedSpelling(
                acceptedSpellingCorrection,
            );
        } else {
            setState((prevState) => ({
                ...prevState,
                acceptedSpellingCorrection: acceptedSpellingCorrection,
            }));
        }
    };

    const query = props.location.queryParams.page_search_query;
    const currentLocale = RequestInfo.kaLocale;
    const isValidLocale = Object.keys(SearchEngines).includes(currentLocale);

    const containerClasses = classnames(
        "search_results",
        css(styles.container),
    );

    const pageTitle = query ? query : t`Search`;

    const tryThisButton = (
        <View style={styles.tryThisLinkContainer}>
            <TryThisButton
                locale={RequestInfo.kaLocale}
                isDefaultState={state.indexedSearchEnabled}
                toggleFunc={() => {
                    setState((prevState) => ({
                        ...prevState,
                        indexedSearchEnabled: !prevState.indexedSearchEnabled,
                    }));

                    const newEngine = !state.indexedSearchEnabled
                        ? "KHAN_ACADEMY"
                        : "GOOGLE";
                    publishAnalyticsEvent({
                        eventType: "SearchEngineChangedTI",
                        eventSchemaVersion: 1,
                        searchTerm: query,
                        newEngine,
                    });
                }}
                tryNotSee={false}
            />
        </View>
    );

    const computerProgramButton = (
        <View style={styles.computerProgramLinkContainer}>
            <ComputerProgramBrowseButton locale={RequestInfo.kaLocale} />
        </View>
    );

    const searchResults = state.indexedSearchEnabled ? (
        <section className="searchresult-nav">
            {tryThisButton}
            {computerProgramButton}
            <IndexedSearch
                query={query}
                googlePoweredDefaultState={state.indexedSearchEnabled}
                googlePoweredToggleFunc={() => {
                    setState((prevState) => ({
                        ...prevState,
                        indexedSearchEnabled: !prevState.indexedSearchEnabled,
                    }));

                    const newEngine = !state.indexedSearchEnabled
                        ? "KHAN_ACADEMY"
                        : "GOOGLE";
                    publishAnalyticsEvent({
                        eventType: "SearchEngineChangedTI",
                        eventSchemaVersion: 1,
                        searchTerm: query,
                        newEngine,
                    });
                }}
            />
        </section>
    ) : (
        <section className="searchresult-nav">
            {tryThisButton}
            <GoogleCustomSearchResults
                isPopup={false}
                openInNewWindow={false}
                query={query}
                kaLocale={RequestInfo.kaLocale}
                focusedIndex={1}
                acceptSpellingCorrection={handleAcceptSpellingCorrection}
            />
        </section>
    );

    return (
        <PageRoot settings={{title: pageTitle, noIndex: true}}>
            <article
                className={containerClasses}
                id="search_results"
                aria-label={t`Search page`}
            >
                <Title style={a11yStyles.srOnly}>{t`Search`}</Title>
                {!isValidLocale && !state.isBannerDismissed && (
                    <View style={{marginBottom: spacing.medium_16}}>
                        <Banner
                            text={t`We don't have support for searching in your current language, sorry! We've done the search in English instead.`}
                            layout="full-width"
                            kind="critical"
                            onDismiss={() => {
                                setState((prevState) => ({
                                    ...prevState,
                                    isBannerDismissed: true,
                                }));
                            }}
                        />
                    </View>
                )}
                <SearchBoxOnly
                    ref={searchBoxElement}
                    placeholder={t`Search for courses, skills, and videos`}
                    initiallyFocused={true}
                    initialQuery={query}
                    extraFormArgs={{search_again: "1"}}
                    onReady={onSearchBoxComponentReady}
                    formClassName={css(styles.searchFormContainer)}
                    inputStyle={styles.searchInput}
                >
                    <Button
                        type="submit"
                        style={styles.searchButton}
                        testId="navbar-search-button"
                    >
                        {t`Search`}
                    </Button>
                </SearchBoxOnly>
                {query && searchResults}
            </article>
        </PageRoot>
    );
};

const styles = StyleSheet.create({
    container: {
        maxWidth: 1200,
        margin: "0 auto",
    },
    tryThisLinkContainer: {
        alignItems: "flex-end",
        [mediaQueries.smOrLarger]: {
            paddingRight: spacing.xLarge_32,
        },
    },
    computerProgramLinkContainer: {
        alignItems: "flex-end",
        [mediaQueries.smOrLarger]: {
            paddingRight: spacing.xLarge_32,
        },
    },
    searchFormContainer: {
        display: "flex",
        gap: spacing.medium_16,
        flexDirection: "row",
    },
    searchInput: {
        flex: 1,
    },
    searchButton: {
        [mediaQueries.smOrLarger]: {
            minWidth: 160,
        },
    },
});

type ExportProps = WithoutApollo<
    WithoutLocation<
        JSX.LibraryManagedAttributes<
            typeof SearchPage,
            React.ComponentProps<typeof SearchPage>
        >
    >
>;

export default compose(
    withStrings,
    withLocation,
    withApollo,
)(SearchPage) as React.ComponentType<ExportProps>;
